export default class EmailChange implements IDigitalIdentityHubModule {
  public name = "EmailChange";

  public init() { /* No functionality */ }

  public ready() {
    // prevents double-submit if emailChange_Confirm.addEventListener called multiple times
    // duplicate listeners are removed by browser, see https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Multiple_identical_event_listeners
    const submitHandler = this.submit.bind(this);

    janrain.events.onCaptureScreenShow.addHandler(result => {
      if (result.screen === "editProfile") {
        // buttons
        const changeEmail = window.DigitalIdentityHub.utilities.getCaptureField("editProfile", "changeEmail");
        if (changeEmail) {
          changeEmail.addEventListener("click", () => this.setModalVisibility(true));
          changeEmail.addEventListener("click", () => this.setSubmitButtonEnabled(true));
        }

        const emailChange_Close = document.getElementById("emailChange_Close");
        if (emailChange_Close) {
          emailChange_Close.addEventListener("click", () => this.setModalVisibility(false));
        }

        const emailChange_Confirm = document.getElementById("emailChange_Confirm");
        if (emailChange_Confirm) {
          emailChange_Confirm.addEventListener("click", submitHandler);
        }

        // validations
        const currentEmail = window.DigitalIdentityHub.utilities.getCaptureField("emailChange", "currentEmail");
        if (currentEmail) {
          currentEmail.addEventListener("blur", () => this.validateField("emailChange", "currentEmail", "Please enter a valid email address", window.DigitalIdentityHub.validations.emailValidation));
          currentEmail.addEventListener("input", () => this.setSubmitButtonEnabled(true));
        }

        const newEmail = window.DigitalIdentityHub.utilities.getCaptureField("emailChange", "newEmail");
        if (newEmail) {
          newEmail.addEventListener("input", () => {
            this.validateField("emailChange", "newEmail", "Please enter a valid email address", window.DigitalIdentityHub.validations.emailValidation);
          });
          newEmail.addEventListener("input", () => this.setSubmitButtonEnabled(true));
        }

        const currentEmailField = this.getCurrentEmailField();
        if (currentEmailField) {
          currentEmailField.innerText = JSON.parse(localStorage.janrainCaptureProfileData || "{}").email;
        }
        const messageField = this.getMessageField();
        if (messageField) {
          messageField.innerText = "";
        }
      }
    });

    janrain.events.onCaptureRenderComplete.addHandler(result => {
      if (result.screen === "emailChangeSuccess") {
        const accessTokenLocalStorage = localStorage.getItem("janrainCaptureToken");
        if (accessTokenLocalStorage) {
          localStorage.removeItem("janrainCaptureToken");
        }
      }
    });

    janrain.events.onCaptureProfileSaveFailed.addHandler(result => {
      if (result.statusMessage === "invalidAccessToken") {
        janrain.capture.ui.endCaptureSession();
      }
    });
  }

  public async submit() {
    if (document.querySelector("#emailChangeModal .capture_error")) {
      this.getMessageField().innerHTML = window.DigitalIdentityHub.utilities.translate("There are validation errors");
      return;
    }

    const newEmail = this.getNewEmailField();
    const password = this.getPasswordField();
    const message = this.getMessageField();

    message.innerText = window.DigitalIdentityHub.utilities.translate("Triggering email change process");
    this.setSubmitButtonEnabled(false);

    // Send an email with activation link
    const trigger = await this.triggerEmail(newEmail.value, password.value, this.getAccessToken(), janrain.settings.capture.clientId);

    this.getMessageField().innerText =
      trigger.error || trigger.sent === false
        ? window.DigitalIdentityHub.utilities.translate("Error triggering email change process")
        : window.DigitalIdentityHub.utilities.translate("Confirmation email sent, check your inbox for further instructions");

    if (trigger.error || trigger.sent === false) {
      this.setSubmitButtonEnabled(true);
    }

    // currently for US: shows a customer help tip after user attempts to trigger email
    // add .sso-hide to your css file:
    // .sso-hide {
    //   display: none;
    //  }
    if (trigger.error) {
      console.log(`EmailChange: ${trigger.error}`);
      const element = document.getElementById("customerHelpTip");
      if (element) {
        element.classList.add("sso-hide");
      }
    }
    if (trigger.sent) {
      const element = document.getElementById("customerHelpTip");
      if (element) {
        element.classList.remove("sso-hide");
      }
    }

    newEmail.value = password.value = "";
  }

  public validateField(fieldName: string, formName: string, errorMessage: string, test: (name, value) => boolean) {
    const ctrl = window.DigitalIdentityHub.utilities.getCaptureField(fieldName, formName);

    const valid = test(fieldName, ctrl.value);
    if (!valid) {
      ctrl.parentElement.classList.add("capture_error");
      const tip = ctrl.parentElement.querySelector<HTMLDivElement>(".capture_tip_error");
      tip.textContent = window.DigitalIdentityHub.utilities.translate(errorMessage);
      tip.style.display = "block";
    } else {
      this.validateFieldUniqueness("emailChange", "newEmail", "traditionalRegistration", "email", "This email address is already in use");
      ctrl.parentElement.classList.remove("capture_error");
      const tip = ctrl.parentElement.querySelector<HTMLDivElement>(".capture_tip_error");
      tip.style.display = "none";
    }
  }

  public validateFieldUniqueness(fieldName: string, formName: string, janrainForm: string, janrainField: string, errorMessage: string) {
    const ctrl = window.DigitalIdentityHub.utilities.getCaptureField(fieldName, formName);
    void window.DigitalIdentityHub.utilities.isUnique(janrainForm, janrainField, ctrl.value).then(unique => {
      if (!unique) {
        ctrl.parentElement.classList.add("capture_error");
        const tip = ctrl.parentElement.querySelector<HTMLDivElement>(".capture_tip_error");
        tip.textContent = window.DigitalIdentityHub.utilities.translate(errorMessage);
        tip.style.display = "block";
      } else {
        ctrl.parentElement.classList.remove("capture_error");
        const tip = ctrl.parentElement.querySelector<HTMLDivElement>(".capture_tip_error");
        tip.style.display = "none";
      }
    });
  }

  /**
   * Fetches access token from Janrain
   *
   * @return {String} Access token
   */
  public getAccessToken(): string {
    const accessToken = localStorage.getItem("janrainCaptureToken");
    if (!accessToken) {
      throw new Error("You must be logged in to use the email change functionality");
    }
    return accessToken;
  }

  /**
   * Triggers a confirmation email using the EmailChangeService
   *
   * @param  {String} email
   * @param  {String} password
   * @param  {String} accessToken
   * @param  {String} clientId
   * @return {Promise<ITriggerEmailResponse>} ECS response
   */
  public async triggerEmail(email, password, accessToken, clientId): Promise<ITriggerEmailResponse> {
    const url = this.createUrl("/emailChangeVerification", {});

    const request = new Request(url, {
      method: "POST",
      headers: {
        "x-api-key": `${window.DigitalIdentityHub.settings.transactionalEmailsKey}`
      },
      body: JSON.stringify({ accessToken, email, password, clientId })
    });

    return fetch(request)
      .then(response => response.json())
      .catch(error => {
        throw new Error(error);
      });
  }

  /**
   * Constructs a URL with parameters
   *
   * @param  {String} path
   * @param  {Object} params
   * @return {URL}
   */
  private createUrl(path: string, params: { [key: string]: string }): URL {
    const url = new URL(window.DigitalIdentityHub.settings.emailChangeEndpoint + path);
    for (const key of Object.keys(params)) {
      url.searchParams.append(key, params[key]);
    }
    return url;
  }

  private setModalVisibility = (visible: boolean) => (document.getElementById("emailChangeModal").style.display = visible ? "block" : "none");
  private getCurrentEmailField = () => window.DigitalIdentityHub.utilities.getCaptureField("emailChange", "currentEmail") as HTMLDivElement;
  private getNewEmailField = () => window.DigitalIdentityHub.utilities.getCaptureField("emailChange", "newEmail");
  private getPasswordField = () => window.DigitalIdentityHub.utilities.getCaptureField("emailChange", "password");
  private getMessageField = () => window.DigitalIdentityHub.utilities.getCaptureField("emailChange", "message") as HTMLDivElement;
  private setSubmitButtonEnabled = (enabled: boolean) => ((document.getElementById("emailChange_Confirm") as HTMLButtonElement).disabled = !enabled);
}

interface ITriggerEmailResponse {
  email: string;
  sent: boolean;
  error: string;
}
